/**
 * IT-support request cards — the pure logic behind the `gyro:support-card` host hook (desktop agent 0.3.9.5; any
 * host later). A host that receives gyro-svc's signed `support-request-*` push frames hands the widget its WHOLE
 * current card list, bounded, on every change:
 *
 *   window.dispatchEvent(new CustomEvent('gyro:support-card', { detail: { cards } }))
 *   window.postMessage({ type: 'gyro:support-card', cards }, '*')        // same-window, like the other hooks
 *
 * The payload is untrusted data from the widget's point of view (any script in the page can dispatch it): every
 * card is re-validated field by field — bounded strings, a numeric clock, an https admin link — and anything
 * malformed drops the card, never the whole list. At most SUPPORT_CARDS_MAX cards are kept, oldest first.
 *
 * Kept free of React/DOM so the state transitions are unit-tested; SupportCard.tsx renders them.
 */
export const SUPPORT_CARDS_MAX = 20;
export const SUPPORT_CARD_LIMITS = {
    requestId: 200,
    shortId: 16,
    summary: 500,
    name: 320,
    email: 320,
    machineName: 200,
    adminUrl: 2048,
    detail: 300,
    updateState: 40,
};
function str(value, max) {
    if (typeof value !== 'string' || value.length > max)
        return null;
    if (/[\r\n\0]/.test(value))
        return null;
    return value;
}
function optionalStr(value, max) {
    if (value === undefined || value === null)
        return undefined;
    const s = str(value, max);
    return s === null ? null : s;
}
function clock(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 8.64e15 ? value : null;
}
/** An absolute https link without credentials — it is opened in a browser tab. */
export function isSafeAdminUrl(value) {
    const s = str(value, SUPPORT_CARD_LIMITS.adminUrl);
    if (s === null || s.length === 0)
        return false;
    try {
        const url = new URL(s);
        return url.protocol === 'https:' && !url.username && !url.password;
    }
    catch {
        return false;
    }
}
/** Validate ONE card; null when it is not a well-formed card of a known kind/state. */
export function parseSupportCard(raw) {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return null;
    const r = raw;
    const requestId = str(r.requestId, SUPPORT_CARD_LIMITS.requestId);
    if (!requestId)
        return null;
    const kindRaw = r.kind === undefined ? 'request' : r.kind;
    if (kindRaw !== 'request' && kindRaw !== 'notice')
        return null;
    const kind = kindRaw;
    const state = r.state === undefined ? (kind === 'notice' ? 'notice' : 'open') : r.state;
    if (state !== 'open' && state !== 'claimed' && state !== 'update' && state !== 'notice')
        return null;
    if ((kind === 'notice') !== (state === 'notice'))
        return null;
    const shortIdRaw = optionalStr(r.shortId, SUPPORT_CARD_LIMITS.shortId);
    if (shortIdRaw === null)
        return null;
    const shortId = shortIdRaw || requestId.slice(0, 8);
    const updateState = optionalStr(r.updateState, SUPPORT_CARD_LIMITS.updateState);
    const detail = optionalStr(r.detail, SUPPORT_CARD_LIMITS.detail);
    if (updateState === null || detail === null)
        return null;
    if (kind === 'notice') {
        return { requestId, shortId, kind, state: 'notice', summary: '', requesterName: '', requesterEmail: '', machineName: '', createdAtMs: 0, adminUrl: '', ...(updateState ? { updateState } : {}), ...(detail ? { detail } : {}) };
    }
    const summaryRaw = optionalStr(r.summary, SUPPORT_CARD_LIMITS.summary);
    const requesterName = str(r.requesterName, SUPPORT_CARD_LIMITS.name);
    const requesterEmailRaw = optionalStr(r.requesterEmail, SUPPORT_CARD_LIMITS.email);
    const machineNameRaw = optionalStr(r.machineName, SUPPORT_CARD_LIMITS.machineName);
    const createdAtMs = clock(r.createdAtMs);
    if (summaryRaw === null || requesterName === null || !requesterName || requesterEmailRaw === null || machineNameRaw === null || createdAtMs === null)
        return null;
    if (!isSafeAdminUrl(r.adminUrl))
        return null;
    const card = { requestId, shortId, kind, state, summary: summaryRaw ?? '', requesterName, requesterEmail: requesterEmailRaw ?? '', machineName: machineNameRaw ?? '', createdAtMs, adminUrl: r.adminUrl };
    if (state !== 'open') {
        const claimedByName = optionalStr(r.claimedByName, SUPPORT_CARD_LIMITS.name);
        const claimedByEmail = optionalStr(r.claimedByEmail, SUPPORT_CARD_LIMITS.email);
        if (claimedByName === null || claimedByEmail === null)
            return null;
        const claimedAtMs = r.claimedAtMs === undefined || r.claimedAtMs === null ? undefined : clock(r.claimedAtMs);
        if (claimedAtMs === null)
            return null;
        if (claimedByName)
            card.claimedByName = claimedByName;
        if (claimedByEmail)
            card.claimedByEmail = claimedByEmail;
        if (claimedAtMs !== undefined)
            card.claimedAtMs = claimedAtMs;
        card.claimedByMe = r.claimedByMe === true;
    }
    if (updateState)
        card.updateState = updateState;
    if (detail)
        card.detail = detail;
    return card;
}
/**
 * Validate a hook payload — the `detail` of the CustomEvent or the posted message — into the card list. Not an
 * object with a `cards` array → null (ignore the event). Malformed cards are dropped individually, duplicates by
 * request id keep the first, and the list is capped at SUPPORT_CARDS_MAX keeping the OLDEST (the host already
 * orders oldest first; a host that sends more than the cap is not honoured beyond it).
 */
export function parseSupportCardsDetail(detail) {
    if (typeof detail !== 'object' || detail === null)
        return null;
    const cards = detail.cards;
    if (!Array.isArray(cards))
        return null;
    const out = [];
    const seen = new Set();
    for (const raw of cards) {
        if (out.length >= SUPPORT_CARDS_MAX)
            break;
        const card = parseSupportCard(raw);
        if (!card || seen.has(card.requestId))
            continue;
        seen.add(card.requestId);
        out.push(card);
    }
    return out;
}
/**
 * Turn the claim route's answer into what the card should say. 200 → this person won; 409 `not_open` → someone
 * else (named when the server says who: its `claimedByName` — the display name — over the `claimedBy` e-mail);
 * 403 `support_operator_required` → not IT staff; 401 → sign in first.
 */
export function claimOutcomeFromResponse(status, body) {
    const b = (typeof body === 'object' && body !== null ? body : {});
    const claimedByName = typeof b.claimedByName === 'string' ? str(b.claimedByName, SUPPORT_CARD_LIMITS.name) ?? undefined : undefined;
    const claimedByEmail = typeof b.claimedBy === 'string' ? str(b.claimedBy, SUPPORT_CARD_LIMITS.email) ?? undefined : undefined;
    const claimedBy = claimedByName || claimedByEmail;
    if (status >= 200 && status < 300)
        return { result: 'won', message: 'You claimed this request.' };
    if (status === 409) {
        return {
            result: 'lost',
            claimedByName: claimedBy,
            message: b.status === 'resolved' ? 'This request was already resolved.' : claimedBy ? `Already claimed by ${claimedBy}.` : 'Someone else claimed this first.',
        };
    }
    if (status === 403 && b.error === 'support_operator_required')
        return { result: 'denied', message: 'Only IT staff can claim support requests.' };
    if (status === 401)
        return { result: 'signin', message: 'Sign in to Gyro first (ask Gyro something), then try again.' };
    if (status === 404)
        return { result: 'lost', message: 'This request no longer exists.' };
    const detail = typeof b.message === 'string' ? str(b.message, 200) : typeof b.error === 'string' ? str(b.error, 100) : null;
    return { result: 'error', message: detail ? `Could not claim (${detail}). Try again.` : 'Could not claim right now. Try again.' };
}
/** The host's card with what this person learned by clicking Claim layered on top, until the host catches up. */
export function applyClaimOutcome(card, outcome) {
    if (!outcome || card.kind !== 'request' || card.state !== 'open')
        return card;
    if (outcome.result === 'won')
        return { ...card, state: 'claimed', claimedByMe: true, claimedByName: card.claimedByName ?? 'you' };
    if (outcome.result === 'lost' && outcome.claimedByName)
        return { ...card, state: 'claimed', claimedByMe: false, claimedByName: outcome.claimedByName };
    return card;
}
/**
 * What a host card list means for the panel. The host re-sends its WHOLE list on every change (and replays it when
 * the widget asks), so "a new request arrived" is: an OPEN request card whose id this person has not seen open
 * before — that opens the panel. A replay, a claim, a resolution or a dismissal never does.
 */
export function acceptHostSupportCards(seenOpenIds, cards) {
    const openIds = new Set(cards.filter((c) => c.kind === 'request' && c.state === 'open').map((c) => c.requestId));
    let fresh = false;
    for (const id of openIds)
        if (!seenOpenIds.has(id))
            fresh = true;
    return { openIds, fresh };
}
/**
 * Keep a local Claim result only while the host still shows that card OPEN: once the host's frame says who claimed it
 * (or the card is gone), the frame is the truth and the click result has done its job. Returns `current` itself when
 * nothing is dropped, so React state does not churn.
 */
export function retainSupportOutcomes(current, cards) {
    const next = {};
    for (const card of cards)
        if (card.kind === 'request' && card.state === 'open' && current[card.requestId])
            next[card.requestId] = current[card.requestId];
    return Object.keys(next).length === Object.keys(current).length ? current : next;
}
/** "IT support request #9c6f0f6e — Pat Doe (E360-PC-12): the printer is offline" */
export function supportCardHeadline(card) {
    const where = card.machineName ? ` (${card.machineName})` : '';
    return `IT support request #${card.shortId} — ${card.requesterName}${where}${card.summary ? `: ${card.summary}` : ''}`;
}
/** A short clock ("14:05") for "Claimed by … at 14:05"; empty when the host sent no time. */
export function formatClaimedAt(ms, locale) {
    if (!ms)
        return '';
    try {
        return new Date(ms).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
    }
    catch {
        return '';
    }
}
/** One line for a session outcome the claimer is told about, or the requester's notice (its detail says it all). */
export function describeUpdateState(state, detail) {
    const base = state === 'consent-denied' ? 'The employee declined the remote session.'
        : state === 'ended' ? 'The remote session ended.'
            : state === 'expired' ? 'The remote session expired.'
                : state === 'claimed' ? 'An IT operator has picked this up.'
                    : '';
    if (!detail)
        return base;
    return base && state !== 'claimed' ? `${base} ${detail}` : detail;
}
//# sourceMappingURL=supportCards.js.map